import { X, FileText, User, Building2, Calendar, Globe, Monitor } from 'lucide-react';
import { AuditLog } from '../../../../services/platform-admin/audit-logs.service';

interface AuditLogDetailModalProps {
  log: AuditLog;
  onClose: () => void;
}

export default function AuditLogDetailModal({ log, onClose }: AuditLogDetailModalProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Audit Log Details</h2>
              <p className="text-sm text-gray-600 mt-1">ID: {log.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Action & Resource */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-sm font-semibold text-gray-900">{log.action}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Resource</label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-sm font-semibold text-gray-900">{log.resource}</span>
                {log.resourceId && (
                  <div className="text-xs text-gray-500 font-mono mt-1">{log.resourceId}</div>
                )}
              </div>
            </div>
          </div>

          {/* Admin & Organization */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Platform Admin
                </div>
              </label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-sm font-semibold text-gray-900">
                  {log.platformAdmin.firstName} {log.platformAdmin.lastName}
                </div>
                <div className="text-xs text-gray-600 mt-1">{log.platformAdmin.email}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Role: {log.platformAdmin.role}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Organization
                </div>
              </label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                {log.organization ? (
                  <>
                    <div className="text-sm font-semibold text-gray-900">
                      {log.organization.name}
                    </div>
                    <div className="text-xs text-gray-600 font-mono mt-1">
                      {log.organization.id}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Status: {log.organization.status}
                    </div>
                  </>
                ) : (
                  <span className="text-sm text-gray-500">Global / No organization</span>
                )}
              </div>
            </div>
          </div>

          {/* Timestamp */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Timestamp
              </div>
            </label>
            <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-sm text-gray-900">{formatDate(log.createdAt)}</div>
            </div>
          </div>

          {/* Changes */}
          {(log.previousValue || log.newValue) && (
            <div className="grid grid-cols-2 gap-6">
              {log.previousValue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Previous Value
                  </label>
                  <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg max-h-64 overflow-y-auto">
                    <pre className="text-xs text-red-900 whitespace-pre-wrap font-mono">
                      {JSON.stringify(log.previousValue, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
              {log.newValue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Value
                  </label>
                  <div className="px-4 py-3 bg-green-50 border border-green-200 rounded-lg max-h-64 overflow-y-auto">
                    <pre className="text-xs text-green-900 whitespace-pre-wrap font-mono">
                      {JSON.stringify(log.newValue, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Request Info */}
          <div className="grid grid-cols-2 gap-6">
            {log.ipAddress && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    IP Address
                  </div>
                </label>
                <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <code className="text-sm text-gray-900 font-mono">{log.ipAddress}</code>
                </div>
              </div>
            )}
            {log.userAgent && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    User Agent
                  </div>
                </label>
                <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="text-xs text-gray-700 break-all">{log.userAgent}</div>
                </div>
              </div>
            )}
          </div>

          {/* Metadata */}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Metadata</label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                <pre className="text-xs text-gray-900 whitespace-pre-wrap font-mono">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
